import { Box, Button, CloseButton, Container, Group, Portal, Stack, Text, Transition, useMantineTheme } from '@mantine/core';
import { IconCheck, IconX, IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { forwardRef, useState } from 'react';
import { BannerAlert as BannerAlertType } from '../providers/alert-types';
import { useReducedMotion } from '../../animations/hooks';
import { pickTextColorForBackground } from '../../../utils/color';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface BannerAlertProps {
  alert: BannerAlertType;
  onDismiss: () => void;
}

const BannerAlert = forwardRef<HTMLDivElement, BannerAlertProps>(({ alert, onDismiss }, ref) => {
  const theme = useMantineTheme();
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(true);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Calculate background and text color first
  const gradient = alert.gradient && alert.gradientVariant ? theme.other.gradients[alert.gradientVariant] : null;
  const backgroundValue = gradient
    ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
    : null;
  const textColor = backgroundValue
    ? pickTextColorForBackground(backgroundValue)
    : null;

  const typeConfig = {
    success: {
      color: theme.colors.green[6],
      icon: <IconCheck size={20} style={{ color: textColor || undefined }} />
    },
    error: {
      color: theme.colors.red[6],
      icon: <IconX size={20} style={{ color: textColor || undefined }} />
    },
    warning: {
      color: theme.colors.yellow[6],
      icon: <IconAlertTriangle size={20} style={{ color: textColor || undefined }} />
    },
    info: {
      color: theme.colors.blue[6],
      icon: <IconInfoCircle size={20} style={{ color: textColor || undefined }} />
    },
  };

  const config = typeConfig[alert.type];

  // Final text color calculation for non-gradient backgrounds
  const finalTextColor = textColor || pickTextColorForBackground(config.color);

  const handleDismiss = () => {
    setVisible(false);
    // Wait for animation to complete before calling onDismiss
    setTimeout(
      () => {
        onDismiss();
      },
      reducedMotion ? 0 : 300
    );
  };

  const getTransition = () => {
    if (alert.position === 'top') {
      return 'slide-down';
    }
    return 'slide-up';
  };

  return (
    <Portal>
      {/* Backdrop */}
      {alert.withBackdrop && (
        <Transition mounted={visible} transition="fade" duration={reducedMotion ? 0 : 200}>
          {(backdropStyles) => (
            <Box
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: alert.withBlur ? 'blur(4px)' : undefined,
                zIndex: 999,
                ...backdropStyles,
              }}
              onClick={alert.dismissible !== false ? handleDismiss : undefined}
            />
          )}
        </Transition>
      )}

      {/* Banner */}
      <Transition
        mounted={visible}
        transition={getTransition()}
        duration={reducedMotion ? 0 : 300}
        timingFunction="ease-out"
      >
        {(styles) => (
          <Box
            ref={ref}
            style={{
              position: 'fixed',
              [alert.position]: 0,
              left: 0,
              right: 0,
              zIndex: alert.withBackdrop ? 1000 : 1000,
              backgroundImage: gradient
                ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
                : undefined,
              backgroundColor: gradient ? undefined : config.color,
              color: finalTextColor,
              boxShadow: theme.shadows.md,
              ...styles,
            }}
          >
            <Container size="xl">
              <Group justify="space-between" py="md" wrap="nowrap">
                <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <Box style={{ flexShrink: 0 }}>{config.icon}</Box>
                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    {alert.title && (
                      <Text fw={600} size="sm" style={{ wordBreak: 'break-word', ...accessibilityStyles }}>
                        {alert.title}
                      </Text>
                    )}
                    <Text size="sm" style={{ wordBreak: 'break-word', ...accessibilityStyles }}>
                      {alert.message}
                    </Text>
                  </Stack>
                </Group>

                <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                  {alert.action && (
                    <Button
                      variant="white"
                      color="dark"
                      onClick={() => {
                        alert.action?.onClick();
                        if (!alert.persistent) {
                          handleDismiss();
                        }
                      }}
                      size="sm"
                    >
                      {alert.action.label}
                    </Button>
                  )}
                  {alert.dismissible !== false && (
                    <CloseButton onClick={handleDismiss} style={{ color: finalTextColor }} />
                  )}
                </Group>
              </Group>
            </Container>
          </Box>
        )}
      </Transition>
    </Portal>
  );
});

BannerAlert.displayName = 'BannerAlert';

export default BannerAlert;
